import {
    ArrowRight,
    Code2,
    ExternalLink,
    FileCode2,
    Globe2,
    Home,
    Layers,
    MessageCircle,
    Plug,
    ShieldCheck,
    Smartphone,
    Sparkles,
    TerminalSquare,
    Users,
    Zap,
  } from "lucide-react";
  import { FaWhatsapp as WhatsApp } from "react-icons/fa";
  
  const WIDGET_CONFIG_PATH = "/developer/widget-config";
  
  const FEATURES = [
    {
      title: "One-line install",
      description:
        "Add the widget to any website using a simple JavaScript script tag.",
      icon: Code2,
    },
    {
      title: "WhatsApp-first flow",
      description:
        "Visitors enter their WhatsApp number and continue directly to Zaviramed doctor chat.",
      icon: WhatsApp,
    },
    {
      title: "Partner tracking",
      description:
        "Track which website, partner, school, company, or campaign generated the lead.",
      icon: Globe2,
    },
    {
      title: "No email required",
      description:
        "The widget is intentionally simple. It collects WhatsApp number and source details only.",
      icon: ShieldCheck,
    },
  ];
  
  const SUPPORTED_PLATFORMS = [
    {
      name: "HTML",
      icon: FileCode2,
    },
    {
      name: "React",
      icon: Layers,
    },
    {
      name: "Next.js",
      icon: Sparkles,
    },
    {
      name: "WordPress",
      icon: Globe2,
    },
    {
      name: "PHP",
      icon: TerminalSquare,
    },
  ];
  
  function Header() {
    return (
      <header className="sticky top-0 z-50 border-b border-slate-100 bg-white/90 backdrop-blur-2xl">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 lg:px-8">
          <a href="/" className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white shadow-sm ring-1 ring-slate-100">
              <img
                src="/logo_new_nobg.png"
                alt="Zaviramed"
                className="h-8 w-8 object-contain"
              />
            </div>
  
            <div>
              <p className="text-lg font-black leading-none text-slate-950">
                Zaviramed
              </p>
              <p className="mt-1 text-[10px] font-black uppercase tracking-[0.22em] text-emerald-600">
                Developers
              </p>
            </div>
          </a>
  
          <nav className="hidden items-center gap-7 lg:flex">
            <a
              href="#tools"
              className="text-sm font-black text-slate-600 hover:text-emerald-700"
            >
              Tools
            </a>
  
            <a
              href="#platforms"
              className="text-sm font-black text-slate-600 hover:text-emerald-700"
            >
              Platforms
            </a>
  
            <a
              href="#workflow"
              className="text-sm font-black text-slate-600 hover:text-emerald-700"
            >
              Workflow
            </a>
          </nav>
  
          <div className="flex items-center gap-3">
            <a
              href="/"
              className="hidden items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-black text-slate-700 transition hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700 sm:inline-flex"
            >
              <Home size={16} />
              Home
            </a>
  
            <a
              href={WIDGET_CONFIG_PATH}
              className="inline-flex items-center gap-2 rounded-2xl bg-slate-950 px-4 py-2.5 text-sm font-black text-white transition hover:bg-emerald-700"
            >
              Open Widget Docs
              <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </header>
    );
  }
  
  function DeveloperHero() {
    return (
      <section className="relative overflow-hidden bg-gradient-to-br from-white via-emerald-50/60 to-white">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(15,23,42,0.045)_1px,transparent_1px),linear-gradient(90deg,rgba(15,23,42,0.045)_1px,transparent_1px)] bg-[size:64px_64px] [mask-image:radial-gradient(ellipse_at_center,white_35%,transparent_78%)]" />
        <div className="absolute -left-52 top-16 h-[420px] w-[420px] rounded-full bg-emerald-300/20 blur-3xl" />
        <div className="absolute -right-52 bottom-0 h-[420px] w-[420px] rounded-full bg-blue-300/10 blur-3xl" />
  
        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 py-20 lg:grid-cols-[1fr_0.8fr] lg:px-8 lg:py-28">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50 px-4 py-2 text-[11px] font-black uppercase tracking-[0.18em] text-emerald-700">
              <Plug size={14} />
              Developer tools
            </div>
  
            <h1 className="mt-7 max-w-3xl text-4xl font-black leading-[1.05] tracking-[-0.045em] text-slate-950 sm:text-6xl lg:text-7xl">
              Build with Zaviramed.
            </h1>
  
            <p className="mt-7 max-w-2xl text-lg font-semibold leading-8 text-slate-600">
              Developer resources for adding Zaviramed healthcare access to
              websites, partner pages, school portals, company wellness pages, and
              digital campaigns.
            </p>
  
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={WIDGET_CONFIG_PATH}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-6 py-3.5 text-sm font-black text-white shadow-xl shadow-emerald-600/25 transition hover:bg-emerald-700"
              >
                View widget documentation
                <ArrowRight size={17} />
              </a>
  
              <a
                href="/"
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-6 py-3.5 text-sm font-black text-slate-700 shadow-sm transition hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700"
              >
                Visit main website
                <ExternalLink size={17} />
              </a>
            </div>
  
            <div className="mt-10 grid max-w-2xl gap-3 sm:grid-cols-3">
              {[
                ["Simple", "One script tag"],
                ["Fast", "WhatsApp redirect"],
                ["Tracked", "Partner/source attribution"],
              ].map(([title, text]) => (
                <div
                  key={title}
                  className="rounded-3xl border border-slate-100 bg-white/85 p-5 shadow-xl shadow-slate-900/5 backdrop-blur"
                >
                  <p className="text-2xl font-black text-slate-950">{title}</p>
                  <p className="mt-1 text-xs font-bold text-slate-500">{text}</p>
                </div>
              ))}
            </div>
          </div>
  
          <DeveloperPreviewCard />
        </div>
      </section>
    );
  }
  
  function DeveloperPreviewCard() {
    return (
      <div className="relative mx-auto w-full max-w-md">
        <div className="rounded-[2rem] border border-slate-100 bg-white p-5 shadow-2xl shadow-slate-900/10">
          <div className="rounded-[1.6rem] bg-slate-950 p-5 text-white">
            <div className="flex items-center justify-between border-b border-white/10 pb-5">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.2em] text-emerald-400">
                  Current tool
                </p>
                <h3 className="mt-2 text-2xl font-black tracking-tight">
                  Website Widget
                </h3>
              </div>
  
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-500 text-white shadow-lg shadow-emerald-500/25">
                <WhatsApp size={25} />
              </div>
            </div>
  
            <div className="mt-5 space-y-3">
              {[
                "Floating WhatsApp doctor chat button",
                "Collects visitor WhatsApp number",
                "Tracks partner, source, page URL, and referrer",
                "Works on HTML, React, Next.js, WordPress, and PHP",
              ].map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <div className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/15">
                    <Zap size={12} className="text-emerald-400" />
                  </div>
                  <p className="text-sm font-semibold leading-6 text-slate-200">
                    {item}
                  </p>
                </div>
              ))}
            </div>
  
            <div className="mt-6 rounded-2xl bg-white/5 p-4 ring-1 ring-white/10">
              <code className="block overflow-x-auto whitespace-nowrap text-xs font-bold text-emerald-200">
                {`<script src="/widget.js" data-partner="my-site" async></script>`}
              </code>
            </div>
          </div>
        </div>
  
        <div className="absolute -bottom-5 right-6 hidden items-center gap-2 rounded-full bg-emerald-600 px-5 py-3 text-sm font-black text-white shadow-xl shadow-emerald-600/30 sm:flex">
          <WhatsApp size={18} />
          Chat with a Doctor
        </div>
      </div>
    );
  }
  
  function ToolCard() {
    return (
      <div className="group relative overflow-hidden rounded-[2rem] border border-slate-100 bg-white p-7 shadow-xl shadow-slate-900/5 transition hover:-translate-y-1 hover:shadow-2xl hover:shadow-slate-900/10">
        <div className="absolute -right-16 -top-16 h-40 w-40 rounded-full bg-emerald-300/15 blur-3xl transition group-hover:bg-emerald-300/25" />
  
        <div className="relative">
          <div className="flex items-start justify-between gap-5">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-600 text-white shadow-lg shadow-emerald-600/25">
              <WhatsApp size={28} />
            </div>
  
            <span className="rounded-full bg-emerald-50 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-emerald-700">
              Available
            </span>
          </div>
  
          <h3 className="mt-7 text-2xl font-black tracking-tight text-slate-950">
            Zaviramed Website Widget
          </h3>
  
          <p className="mt-4 text-sm font-semibold leading-7 text-slate-600">
            Add a floating “Chat with a Doctor” button to any website. The widget
            captures the visitor’s WhatsApp number, tracks the partner/source, and
            redirects them to Zaviramed on WhatsApp.
          </p>
  
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            {[
              "No email field",
              "One script tag",
              "Partner tracking",
              "Mobile responsive",
            ].map((item) => (
              <div
                key={item}
                className="flex items-center gap-2 rounded-2xl bg-slate-50 px-3 py-2.5"
              >
                <div className="h-2 w-2 rounded-full bg-emerald-500" />
                <span className="text-xs font-black text-slate-600">{item}</span>
              </div>
            ))}
          </div>
  
          <a
            href={WIDGET_CONFIG_PATH}
            className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-950 px-5 py-3.5 text-sm font-black text-white transition hover:bg-emerald-700"
          >
            Open documentation
            <ArrowRight size={17} />
          </a>
        </div>
      </div>
    );
  }
  
  function ToolsSection() {
    return (
      <section id="tools" className="py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-sm font-black uppercase tracking-[0.2em] text-emerald-600">
              Available developer tool
            </p>
  
            <h2 className="mt-4 text-3xl font-black tracking-tight text-slate-950 sm:text-5xl">
              Start with the Zaviramed Website Widget.
            </h2>
  
            <p className="mt-5 text-base font-semibold leading-8 text-slate-600">
              For now, the developer portal focuses on one useful integration:
              adding Zaviramed doctor chat access to external websites.
            </p>
          </div>
  
          <div className="mx-auto mt-12 max-w-2xl">
            <ToolCard />
          </div>
        </div>
      </section>
    );
  }
  
  function FeaturesSection() {
    return (
      <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {FEATURES.map(({ title, description, icon: Icon }) => (
              <div
                key={title}
                className="rounded-[1.7rem] border border-slate-100 bg-white p-6 shadow-xl shadow-slate-900/5"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700">
                  <Icon size={22} />
                </div>
  
                <h3 className="mt-5 text-lg font-black text-slate-950">
                  {title}
                </h3>
  
                <p className="mt-3 text-sm font-semibold leading-7 text-slate-600">
                  {description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }
  
  function PlatformsSection() {
    return (
      <section id="platforms" className="py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-[0.8fr_1fr]">
            <div>
              <p className="text-sm font-black uppercase tracking-[0.2em] text-emerald-600">
                Platform support
              </p>
  
              <h2 className="mt-4 text-3xl font-black tracking-tight text-slate-950 sm:text-5xl">
                Designed for normal websites and modern apps.
              </h2>
  
              <p className="mt-5 text-base font-semibold leading-8 text-slate-600">
                Developers can install the widget on plain HTML websites, React
                apps, Next.js apps, WordPress websites, PHP templates, and landing
                pages.
              </p>
  
              <a
                href={WIDGET_CONFIG_PATH}
                className="mt-8 inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-6 py-3.5 text-sm font-black text-white shadow-xl shadow-emerald-600/25 transition hover:bg-emerald-700"
              >
                See implementation examples
                <ArrowRight size={17} />
              </a>
            </div>
  
            <div className="grid gap-4 sm:grid-cols-2">
              {SUPPORTED_PLATFORMS.map(({ name, icon: Icon }) => (
                <div
                  key={name}
                  className="flex items-center gap-4 rounded-3xl border border-slate-100 bg-white p-5 shadow-xl shadow-slate-900/5"
                >
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-slate-50 text-emerald-700">
                    <Icon size={22} />
                  </div>
  
                  <div>
                    <p className="font-black text-slate-950">{name}</p>
                    <p className="mt-1 text-xs font-bold text-slate-500">
                      Supported
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }
  
  function WorkflowSection() {
    return (
      <section id="workflow" className="bg-slate-950 py-20 text-white">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-sm font-black uppercase tracking-[0.2em] text-emerald-400">
              How it works
            </p>
  
            <h2 className="mt-4 text-3xl font-black tracking-tight sm:text-5xl">
              From partner website to doctor chat.
            </h2>
  
            <p className="mt-5 text-base font-semibold leading-8 text-slate-300">
              The widget reduces friction. It does not ask unnecessary questions.
              It collects the WhatsApp number, records attribution, and moves the
              visitor to chat.
            </p>
          </div>
  
          <div className="mt-14 grid gap-5 md:grid-cols-4">
            {[
              {
                title: "Install",
                text: "Developer adds the script to the website.",
                icon: Code2,
              },
              {
                title: "Visitor clicks",
                text: "The floating WhatsApp button opens the widget.",
                icon: MessageCircle,
              },
              {
                title: "Number captured",
                text: "Visitor enters WhatsApp number only.",
                icon: Smartphone,
              },
              {
                title: "Redirect",
                text: "Visitor continues to Zaviramed doctor chat.",
                icon: WhatsApp,
              },
            ].map(({ title, text, icon: Icon }, index) => (
              <div
                key={title}
                className="rounded-[1.7rem] border border-white/10 bg-white/5 p-6 backdrop-blur"
              >
                <div className="flex items-center justify-between">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-500 text-white">
                    <Icon size={22} />
                  </div>
  
                  <span className="text-3xl font-black text-white/10">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                </div>
  
                <h3 className="mt-6 text-lg font-black text-white">{title}</h3>
                <p className="mt-3 text-sm font-semibold leading-7 text-slate-400">
                  {text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }
  
  function FooterCTA() {
    return (
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="rounded-[2.5rem] bg-gradient-to-br from-emerald-600 to-emerald-700 p-8 text-white shadow-2xl shadow-emerald-600/20 sm:p-12">
            <div className="grid items-center gap-8 lg:grid-cols-[1fr_auto]">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-[11px] font-black uppercase tracking-[0.18em] text-white">
                  <Users size={14} />
                  Partner-ready
                </div>
  
                <h2 className="mt-5 max-w-2xl text-3xl font-black tracking-tight sm:text-5xl">
                  Add Zaviramed doctor chat to a partner website.
                </h2>
  
                <p className="mt-5 max-w-2xl text-base font-semibold leading-8 text-emerald-50">
                  Use the widget documentation to install, configure partner
                  tracking, and test the WhatsApp redirect flow.
                </p>
              </div>
  
              <a
                href={WIDGET_CONFIG_PATH}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-6 py-4 text-sm font-black text-emerald-700 shadow-sm transition hover:bg-emerald-50"
              >
                Open Widget Docs
                <ArrowRight size={17} />
              </a>
            </div>
          </div>
        </div>
      </section>
    );
  }
  
  export default function DeveloperHome() {
    return (
      <main className="min-h-screen bg-white text-slate-950">
        <style>{`
          html {
            scroll-behavior: smooth;
          }
  
          body,
          button,
          input,
          textarea,
          select {
            font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
          }
        `}</style>
  
        <Header />
        <DeveloperHero />
        <ToolsSection />
        <FeaturesSection />
        <PlatformsSection />
        <WorkflowSection />
        <FooterCTA />
      </main>
    );
  }